/**
 * Green Bubble Points in the webshop.
 *
 * The points estimate lives in one place, here (pointsFor). Liquid renders the same
 * number without a campaign as the initial value in snippets/loyalty-points.liquid; this
 * script then updates it to the current price and applies a running
 * bonus points campaign.
 *
 * Why in the browser: Shopify caches product pages, so a time window
 * that Liquid computes can switch on or off hours late. And the price on the
 * product page changes without a reload (size, pot), through scripts that rewrite the
 * price DOM; we follow those with a MutationObserver instead of
 * adding a call to each of those scripts.
 *
 * Since 13 October 2026 Smile calculates over the amount including VAT and the
 * VAT margin is 0: the number is then what Smile gives for Brons without a
 * discount code, rounded down. If Smile calculates without VAT again, restore the
 * margin, so the number is never higher than what the customer gets.
 */
(function (root) {
  "use strict";

  function pointsFor(amountCents, config) {
    var cents = Number(amountCents);

    if (!isFinite(cents) || cents <= 0) return 0;
    var rate = Number(config.pointsPerEuro) || 0;
    var margin = Number(config.vatMarginPercent) || 0;
    var multiplier = Number(config.multiplier) || 1;
    // Integers, just like in Liquid: cents × points per euro ÷ (100 + margin).
    var base = Math.floor((cents * rate) / (100 + margin));

    return base * multiplier;
  }

  function campaignActive(campaign, now) {
    if (!campaign || !campaign.enabled) return false;
    var start = Date.parse(campaign.start);
    var end = Date.parse(campaign.end);

    if (!isFinite(start) || !isFinite(end) || end <= start) return false;
    var t = now instanceof Date ? now.getTime() : Number(now);

    return t >= start && t <= end;
  }

  function parsePriceText(text) {
    // The price sits in separate spans for euros and cents, sometimes without a comma
    // in between ("€34 95"), sometimes with one ("€1.234,95"). Hence digit groups: the
    // last group of two digits is the cents, the rest is euros.
    var groups = String(text || "").match(/\d+/g);

    if (!groups) return null;
    var cents = 0;

    if (groups.length > 1 && groups[groups.length - 1].length === 2) {
      cents = Number(groups.pop());
    }

    var value = Number(groups.join("")) * 100 + cents;

    return isFinite(value) ? value : null;
  }

  function groupDigits(n, separator) {
    // The same formatting as snippets/loyalty-format.liquid: groups of three digits with the
    // separator of the language. Intl.NumberFormat does not group four digits in Polish and
    // Spanish, and then the number would change formatting after loading.
    var digits = String(Math.max(0, Math.floor(Number(n) || 0)));
    var out = "";

    for (var i = 0; i < digits.length; i++) {
      if (i > 0 && (digits.length - i) % 3 === 0) out += separator;
      out += digits.charAt(i);
    }

    return out;
  }

  var api = {
    pointsFor: pointsFor,
    campaignActive: campaignActive,
    parsePriceText: parsePriceText,
    groupDigits: groupDigits,
  };

  // A CommonJS `module` object, as `typeof module === "object"` would check.
  var commonJs =
    typeof module !== "undefined" && Object(module) === module && !(module instanceof Function);

  if (commonJs && module.exports) {
    module.exports = api;

    return;
  }

  // `page: true` without `enabled`: the loyalty page in a market that does not pay in
  // euros (snippets/loyalty-config.liquid). Then only initPage runs.
  var config = root.GB_LOYALTY;

  if (!config || !(config.enabled || config.page)) return;
  var doc = root.document;

  function formatNumber(n) {
    return groupDigits(n, config.thousandsSeparator == null ? "." : config.thousandsSeparator);
  }

  function formatDate(iso) {
    try {
      return new Intl.DateTimeFormat(config.locale, { day: "numeric", month: "long" }).format(
        new Date(iso),
      );
    } catch (error) {
      // Intl rejects an unknown locale or an invalid date: show no date then. The
      // binding is unused; the optional catch binding (ES2019) would work as well.
      void error;

      return "";
    }
  }

  function currentMultiplier() {
    return campaignActive(config.campaign, new Date())
      ? Number(config.campaign.multiplier) || 1
      : 1;
  }

  function amountFor(el) {
    // Selectors in order of preference, separated by "||": the first one that is on the
    // page and contains a price counts.
    var sources = (el.getAttribute("data-loyalty-price-source") || "").split("||");

    for (var i = 0; i < sources.length; i++) {
      var selector = sources[i].trim();
      var source = selector ? doc.querySelector(selector) : null;
      var parsed = source ? parsePriceText(source.textContent) : null;

      if (parsed !== null) return parsed;
    }

    return Number(el.getAttribute("data-loyalty-amount"));
  }

  function render() {
    var multiplier = currentMultiplier();
    var active = multiplier > 1;
    doc.querySelectorAll(".js-loyalty-points").forEach(function (el) {
      var points = pointsFor(amountFor(el), {
        pointsPerEuro: config.pointsPerEuro,
        vatMarginPercent: config.vatMarginPercent,
        multiplier: multiplier,
      });

      var count = el.querySelector(".js-loyalty-points-count");

      if (count) count.textContent = formatNumber(points);
      el.hidden = points <= 0;
      var bonus = el.querySelector(".js-loyalty-points-bonus");

      if (bonus) bonus.hidden = !active;
    });
    doc.querySelectorAll(".js-loyalty-campaign-multiplier").forEach(function (el) {
      el.textContent = String(config.campaign.multiplier);
    });
    // The server fills in the date in the page language; we only fill an empty date here.
    doc.querySelectorAll(".js-loyalty-campaign-date").forEach(function (el) {
      if (!el.textContent.trim()) el.textContent = formatDate(config.campaign.end);
    });
    doc.querySelectorAll(".js-loyalty-campaign").forEach(function (el) {
      el.hidden = !active;
    });
  }

  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    root.requestAnimationFrame(function () {
      scheduled = false;
      render();
    });
  }

  /*
   * Loyalty page (sections/page-loyalty.liquid): the savings calculator, the filter on the
   * rewards, the counting-up number on the card and the cards coming in. The page is
   * complete without this script: Liquid renders the initial values and all rewards.
   */
  var reduceMotion = root.matchMedia && root.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function initCalculator(el) {
    var rates = (el.getAttribute("data-rates") || "").split(",").map(Number);
    var margin = Number(el.getAttribute("data-margin")) || 0;
    var rewardPoints = Number(el.getAttribute("data-reward-points")) || 0;
    var rewardValue = Number(el.getAttribute("data-reward-value")) || 0;
    var separator = el.getAttribute("data-separator") || "";
    var decimal = el.getAttribute("data-decimal") || ",";
    var euro = el.getAttribute("data-euro") || "__AMOUNT__";
    var range = el.querySelector(".js-lp-calc-range");

    if (!range || rewardPoints <= 0) return;

    var parts = {
      amount: el.querySelector(".js-lp-calc-amount"),
      points: el.querySelector(".js-lp-calc-points"),
      worth: el.querySelector(".js-lp-calc-worth"),
      percent: el.querySelector(".js-lp-calc-percent"),
      fill: el.querySelector(".js-lp-calc-fill"),
      advice: el.querySelector(".js-lp-calc-advice"),
    };

    function euros(cents, withCents) {
      var whole = groupDigits(Math.floor(cents / 100), separator);

      if (withCents) {
        var rest = Math.floor(cents % 100);
        whole += decimal + (rest < 10 ? "0" : "") + rest;
      }

      return euro.replace("__AMOUNT__", whole);
    }

    function setText(node, text) {
      if (node && node.textContent !== text) node.textContent = text;
    }

    function update() {
      var checked = el.querySelector(".js-lp-calc-tier:checked");
      var rate = rates[checked ? Number(checked.value) : 0] || rates[0] || 0;
      var amount = Number(range.value) || 0;
      var points = pointsFor(amount * 100, { pointsPerEuro: rate, vatMarginPercent: margin });
      var worthCents = Math.floor((points * rewardValue * 100) / rewardPoints);
      var percent = Math.min(100, Math.floor((points * 100) / rewardPoints));
      var advice = "";

      if (points >= rewardPoints) {
        var rewards = Math.floor(points / rewardPoints);
        advice = el
          .getAttribute("data-enough")
          .replace("__VALUE__", euros(rewards * rewardValue * 100, false));
      } else if (points > 0) {
        var more = Math.ceil(rewardPoints / points) - 1;
        advice =
          more === 1
            ? el.getAttribute("data-one")
            : el.getAttribute("data-more").replace("__COUNT__", String(more));
      }

      var oldPoints = parts.points ? parts.points.textContent : "";
      setText(parts.amount, euros(amount * 100, false));
      // Otherwise a screen reader announces only the bare number for the slider.
      range.setAttribute("aria-valuetext", euros(amount * 100, false));
      setText(parts.points, groupDigits(points, separator));
      setText(
        parts.worth,
        el.getAttribute("data-worth").replace("__VALUE__", euros(worthCents, true)),
      );
      setText(parts.percent, percent >= 100 ? el.getAttribute("data-reached") : percent + "%");

      if (parts.fill) parts.fill.style.width = percent + "%";
      setText(parts.advice, advice);

      if (parts.points && oldPoints !== parts.points.textContent && !reduceMotion) {
        parts.points.classList.remove("is-bumped");
        void parts.points.offsetWidth;
        parts.points.classList.add("is-bumped");
      }
    }

    range.addEventListener("input", update);
    el.querySelectorAll(".js-lp-calc-tier").forEach(function (input) {
      input.addEventListener("change", update);
    });
    update();
  }

  function initFilter(group) {
    var list = doc.querySelector(".js-lp-rewards");

    if (!list) return;
    var buttons = group.querySelectorAll("[data-lp-filter]");
    group.hidden = false;
    buttons.forEach(function (button) {
      button.addEventListener("click", function () {
        var kind = button.getAttribute("data-lp-filter");
        buttons.forEach(function (other) {
          other.setAttribute("aria-pressed", other === button ? "true" : "false");
        });
        list.querySelectorAll("[data-lp-kind]").forEach(function (item) {
          item.hidden = kind !== "all" && item.getAttribute("data-lp-kind") !== kind;
        });
      });
    });
  }

  function countUp(el) {
    var target = Number(el.getAttribute("data-lp-countup")) || 0;
    var separator = config.thousandsSeparator == null ? "." : config.thousandsSeparator;

    if (reduceMotion || !root.requestAnimationFrame) {
      el.textContent = groupDigits(target, separator);

      return;
    }

    var begin = null;

    function frame(now) {
      if (begin === null) begin = now;
      var t = Math.min(1, (now - begin) / 1600);
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = groupDigits(Math.round(target * eased), separator);

      if (t < 1) root.requestAnimationFrame(frame);
    }

    root.requestAnimationFrame(frame);
  }

  function initPage() {
    var page = doc.querySelector(".js-lp");

    if (!page) return;
    page.querySelectorAll(".js-lp-calc").forEach(initCalculator);
    page.querySelectorAll(".js-lp-filter").forEach(initFilter);
    page.querySelectorAll(".js-lp-countup").forEach(countUp);

    if (reduceMotion || !("IntersectionObserver" in root)) return;

    var observer = new root.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );

    page.querySelectorAll(".lp-reveal").forEach(function (item, index) {
      item.style.transitionDelay = (index % 4) * 80 + "ms";
      observer.observe(item);
    });
    page.classList.add("is-ready");
  }

  // On desktop (mouse, hover) the link to the full page opens in a new
  // tab, so the product page and cart stay open. Not on mobile: there
  // almost nobody finds the way back from a second tab. The same media query
  // is in assets/gb-loyalty.css, which shows the icon and the screen reader text.
  var NEW_TAB_QUERY = "(hover: hover) and (pointer: fine)";

  function opensInNewTab() {
    return !!(root.matchMedia && root.matchMedia(NEW_TAB_QUERY).matches);
  }

  function onNewTabClick(event) {
    var link = event.target && event.target.closest && event.target.closest("a.js-loyalty-new-tab");

    if (!link || event.defaultPrevented || event.button !== 0) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    if (!opensInNewTab()) return;
    event.preventDefault();
    var opened = root.open(link.href, "_blank");

    if (opened) {
      opened.opener = null;
    } else {
      // Pop-up blocker: open it normally after all instead of doing nothing.
      root.location.href = link.href;
    }
  }

  function start() {
    initPage();

    if (!config.enabled) return;
    render();
    // Delegated, because the minicart is reloaded after every change.
    doc.addEventListener("click", onNewTabClick);
    // The minicart is reloaded after every change and the product price
    // changes with the chosen size or pot; both rewrite the DOM.
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;

        if (target && target.closest && target.closest(".js-loyalty-points, .js-loyalty-campaign"))
          continue;
        schedule();

        return;
      }
    }).observe(doc.body, { childList: true, subtree: true, characterData: true });
    // A campaign that starts or ends while the page is open.
    root.setInterval(render, 60000);
  }

  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})(typeof window !== "undefined" ? window : this);
